import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  XMarkIcon,
  StarIcon,
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarSolidIcon } from '@heroicons/react/24/solid';
import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import { useLanguageStore } from '../../stores/languageStore';

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  lawsuitId: string;
  lawsuitTitle: string;
}

interface FeedbackData {
  rating: number;
  comment: string;
  category: 'general' | 'accuracy' | 'completeness' | 'usability' | 'suggestions';
  isAnonymous: boolean;
}

const FeedbackModal: React.FC<FeedbackModalProps> = ({
  isOpen,
  onClose,
  lawsuitId,
  lawsuitTitle,
}) => {
  const { t } = useTranslation();
  const { isRTL } = useLanguageStore();
  
  const [formData, setFormData] = useState<FeedbackData>({
    rating: 0,
    comment: '',
    category: 'general',
    isAnonymous: false,
  });
  
  const [hoveredRating, setHoveredRating] = useState(0);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const feedbackMutation = useMutation({
    mutationFn: async (data: FeedbackData & { lawsuitId: string }) => {
      const response = await axios.post('/api/feedback', data);
      return response.data;
    },
    onSuccess: () => {
      setIsSubmitted(true);
      setTimeout(() => {
        handleClose();
      }, 2000);
    },
  });

  const handleClose = () => {
    setFormData({
      rating: 0,
      comment: '',
      category: 'general',
      isAnonymous: false,
    });
    setHoveredRating(0);
    setIsSubmitted(false);
    onClose();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.rating === 0 || !formData.comment.trim()) {
      return;
    }

    feedbackMutation.mutate({
      ...formData,
      lawsuitId,
      comment: formData.comment.trim(),
    });
  };

  const handleRatingClick = (rating: number) => {
    setFormData(prev => ({ ...prev, rating }));
  };

  const categories = [
    { value: 'general' as const, label: t('feedback.categories.general', 'General Feedback') },
    { value: 'accuracy' as const, label: t('feedback.categories.accuracy', 'Document Accuracy') },
    { value: 'completeness' as const, label: t('feedback.categories.completeness', 'Completeness') },
    { value: 'usability' as const, label: t('feedback.categories.usability', 'Usability') },
    { value: 'suggestions' as const, label: t('feedback.categories.suggestions', 'Suggestions') },
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={handleClose} />
        
        <div className={`relative bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center">
              <ChatBubbleLeftRightIcon className="h-6 w-6 text-blue-600 mr-3" />
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {t('feedback.title', 'Share Your Feedback')}
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  {t('feedback.subtitle', 'Help us improve your experience with')} "{lawsuitTitle}"
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              disabled={feedbackMutation.isPending}
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {isSubmitted ? (
              // Success state
              <div className="text-center py-8">
                <CheckCircleIcon className="h-16 w-16 text-green-500 mx-auto mb-4" />
                <h4 className="text-xl font-semibold text-gray-900 mb-2">
                  {t('feedback.success.title', 'Thank You!')}
                </h4>
                <p className="text-gray-600">
                  {t('feedback.success.message', 'Your feedback has been submitted successfully. We appreciate your input!')}
                </p>
              </div>
            ) : (
              // Form
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Rating */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    {t('feedback.rating.label', 'How would you rate your overall experience?')}
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                  <div className="flex items-center space-x-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => handleRatingClick(star)}
                        onMouseEnter={() => setHoveredRating(star)}
                        onMouseLeave={() => setHoveredRating(0)}
                        className="p-1 transition-transform hover:scale-110"
                        disabled={feedbackMutation.isPending}
                      >
                        {(hoveredRating >= star || formData.rating >= star) ? (
                          <StarSolidIcon className="h-8 w-8 text-yellow-400" />
                        ) : (
                          <StarIcon className="h-8 w-8 text-gray-300" />
                        )}
                      </button>
                    ))}
                    {formData.rating > 0 && (
                      <span className="ml-3 text-sm text-gray-600">
                        {formData.rating} {t('feedback.rating.outOf', 'out of')} 5
                      </span>
                    )}
                  </div>
                  {formData.rating === 0 && (
                    <p className="text-sm text-red-600 mt-1">
                      {t('feedback.rating.required', 'Please select a rating')}
                    </p>
                  )}
                </div>

                {/* Category */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('feedback.category.label', 'Feedback Category')}
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value as any }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    disabled={feedbackMutation.isPending}
                  >
                    {categories.map((category) => (
                      <option key={category.value} value={category.value}>
                        {category.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('feedback.comment.label', 'Your Comments')}
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                  <textarea
                    value={formData.comment}
                    onChange={(e) => setFormData(prev => ({ ...prev, comment: e.target.value }))}
                    placeholder={t('feedback.comment.placeholder', 'Please share your thoughts, suggestions, or any issues you encountered...')}
                    rows={4}
                    maxLength={2000}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 resize-vertical"
                    disabled={feedbackMutation.isPending}
                  />
                  <div className="flex justify-between items-center mt-1">
                    <div>
                      {!formData.comment.trim() && (
                        <p className="text-sm text-red-600">
                          {t('feedback.comment.required', 'Please provide your feedback')}
                        </p>
                      )}
                    </div>
                    <p className="text-xs text-gray-500">
                      {formData.comment.length}/2000
                    </p>
                  </div>
                </div>

                {/* Anonymous option */}
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="anonymous"
                    checked={formData.isAnonymous}
                    onChange={(e) => setFormData(prev => ({ ...prev, isAnonymous: e.target.checked }))}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    disabled={feedbackMutation.isPending}
                  />
                  <label htmlFor="anonymous" className="ml-2 block text-sm text-gray-700">
                    {t('feedback.anonymous.label', 'Submit anonymously')}
                  </label>
                </div>

                {/* Error message */}
                {feedbackMutation.error && (
                  <div className="flex items-center p-3 bg-red-50 border border-red-200 rounded-md">
                    <ExclamationTriangleIcon className="h-5 w-5 text-red-500 mr-2" />
                    <p className="text-sm text-red-700">
                      {(feedbackMutation.error as any)?.response?.data?.error || 
                       t('feedback.error.generic', 'Failed to submit feedback. Please try again.')}
                    </p>
                  </div>
                )}

                {/* Actions */}
                <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    disabled={feedbackMutation.isPending}
                  >
                    {t('common.cancel', 'Cancel')}
                  </button>
                  <button
                    type="submit"
                    disabled={
                      feedbackMutation.isPending || 
                      formData.rating === 0 || 
                      !formData.comment.trim()
                    }
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {feedbackMutation.isPending ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        {t('feedback.submitting', 'Submitting...')}
                      </>
                    ) : (
                      t('feedback.submit', 'Submit Feedback')
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeedbackModal;